/**
 * Guest-related validation utilities for Team Housing Solutions
 * These validators work with DoD IDs, client identifiers, and guest data
 */

export const DOD_ID_FORMAT_DESCRIPTION =
  '7 to 11 digits, or 2 or 3 letters followed by 4 digits';

export const DOD_ID_VALIDATION_MESSAGE = `Invalid DOD ID. Must be ${DOD_ID_FORMAT_DESCRIPTION}`;

export const DOD_ID_NUMERIC_PATTERN = /^\d{7,11}$/;
export const DOD_ID_LETTER_DIGIT_PATTERN = /^[A-Za-z]{2,3}\d{4}$/i;

/**
 * Validates if a string is a valid DoD ID (Department of Defense ID)
 *
 * Accepts:
 * - 7 to 11 digits (numeric DOD ID format): 1234567, 1234567890
 * - 2 or 3 letters + 4 digits (TXSG format): TX1234, ABC1234
 * - Optional: Client subgroups (if provided)
 *
 * @param dodid - The DoD ID to validate
 * @param validClientSubgroups - Optional array of valid client subgroup identifiers
 * @returns true if the DoD ID matches any valid format
 *
 * @example
 * isValidDODID('1234567890') // true - numeric EDIPI
 * isValidDODID('TX1234') // true - TXSG-style format
 * isValidDODID('ABC1234') // true - TXSG-style format
 * isValidDODID('tx1234') // true - case insensitive
 * isValidDODID('ABC', ['ABC', 'XYZ']) // true - valid subgroup
 * isValidDODID('invalid') // false
 */
export const isValidDODID = (
  dodid?: string | null,
  validClientSubgroups?: string[],
): boolean => {
  if (!dodid) {
    return false;
  }

  const trimmed = dodid.trim();

  // Check standard DoD ID formats
  if (
    DOD_ID_NUMERIC_PATTERN.test(trimmed) ||
    DOD_ID_LETTER_DIGIT_PATTERN.test(trimmed)
  ) {
    return true;
  }

  // Check if it's a valid client subgroup (case-insensitive)
  if (validClientSubgroups && validClientSubgroups.length > 0) {
    const upperDodid = trimmed.toUpperCase();
    const upperSubgroups = validClientSubgroups.map((sg) => sg.toUpperCase());
    if (upperSubgroups.includes(upperDodid)) {
      return true;
    }
  }

  return false;
};

/**
 * Simple heuristic check to determine if input looks like it could be a DoD ID
 *
 * This is a less strict validation used for:
 * - Search input validation
 * - Determining whether to treat input as ID vs name
 * - Pre-validation before sending to backend
 *
 * Returns true if input:
 * - Contains only digits (partial or complete EDIPI)
 * - Matches TXSG-style pattern (2 or 3 letters + 4 digits)
 * - Looks like it could be a client subgroup identifier
 *
 * @param input - The string to check
 * @returns true if the input looks like it could be a DoD ID
 *
 * @example
 * looksLikeDODID('123') // true - numeric input
 * looksLikeDODID('1234567890') // true - full EDIPI
 * looksLikeDODID('TX1234') // true - TXSG-style format
 * looksLikeDODID('ABC1234') // true - TXSG-style format
 * looksLikeDODID('John Smith') // false - looks like a name
 * looksLikeDODID('ABC') // true - could be subgroup
 */
export const looksLikeDODID = (input?: string | null): boolean => {
  if (!input) {
    return false;
  }

  const trimmed = input.trim();

  // Check if it's purely numeric (traditional DoD ID or partial)
  if (/^\d+$/.test(trimmed)) {
    return true;
  }

  // Check if it matches the TXSG-style format pattern (2 or 3 letters + 4 digits)
  if (DOD_ID_LETTER_DIGIT_PATTERN.test(trimmed)) {
    return true;
  }

  // Check if it could be a client subgroup identifier
  // (short alphanumeric codes, typically 2-3 characters or mixed alphanumeric)
  // Exclude single words that look like names (4+ letters with no numbers)
  if (/^[A-Za-z0-9]{2,6}$/i.test(trimmed) && /[A-Za-z]/.test(trimmed)) {
    // If it's 4+ characters and ALL letters, it's probably a name, not a DoD ID
    if (trimmed.length >= 4 && /^[A-Za-z]+$/.test(trimmed)) {
      return false;
    }
    return true;
  }

  return false;
};

/**
 * Validates guest name format
 *
 * @param firstName - Guest's first name
 * @param lastName - Guest's last name
 * @returns true if both names are non-empty strings
 */
export const isValidGuestName = (
  firstName?: string | null,
  lastName?: string | null,
): boolean => {
  return (
    !!firstName &&
    firstName.trim().length > 0 &&
    !!lastName &&
    lastName.trim().length > 0
  );
};

/**
 * Formats a DoD ID for display (uppercase for consistency)
 *
 * @param dodid - The DoD ID to format
 * @returns Formatted DoD ID in uppercase, or empty string if invalid
 */
export const formatDODID = (dodid?: string | null): string => {
  if (!dodid) {
    return '';
  }
  return dodid.trim().toUpperCase();
};
